/**
 * Cart Drawer Bonus Threshold Bar
 * Displays a progress bar in the slide-out cart drawer showing how close
 * the user is to unlocking free bonus entries at dollar milestones.
 *
 * Data source: /cart.js (always fresh, no stale DOM dependency)
 * Updates on: drawer open, cart:updated, cart:item-added, DOMContentLoaded
 *
 * The bar container lives inside cart-items-component and gets replaced on
 * section refresh. This script lives OUTSIDE that component, so it
 * persists across refreshes. On each update it checks whether the inner
 * content needs to be rebuilt.
 */
(function () {
  'use strict';

  /* ── Configuration ─────────────────────────────────────────────────── */
  var MILESTONES = [
    { dollars: 50, entries: 200 },
    { dollars: 150, entries: 800 }
  ];

  var DEBOUNCE_MS = 300;

  /* ── State ─────────────────────────────────────────────────────────── */
  var debounceTimer = null;
  var listenersAttached = false;

  /* ── Helpers ────────────────────────────────────────────────────────── */
  function getMaxDollars() {
    return MILESTONES[MILESTONES.length - 1].dollars;
  }

  function withCommas(n) {
    return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /* ── Build the bar inner HTML ──────────────────────────────────────── */
  function buildContentHTML() {
    var maxDollars = getMaxDollars();
    var html = '';

    // Track wrapper — contains fill bar and milestone groups
    html += '<div class="bonus-bar__track-wrap">';

    // Milestone labels positioned above track
    MILESTONES.forEach(function (ms, i) {
      var leftPct = (ms.dollars / maxDollars) * 100;
      var isLast = i === MILESTONES.length - 1;
      var alignClass = isLast ? ' bonus-bar__label--end' : '';
      html += '<span class="bonus-bar__label' + alignClass + '" data-index="' + i + '" style="left:' + leftPct + '%">';
      html += withCommas(ms.entries) + ' Free Entries';
      html += '</span>';
    });

    // Track bar
    html += '<div class="bonus-bar__track">';
    html += '<div class="bonus-bar__fill"></div>';

    // Markers with dollar amount inside
    MILESTONES.forEach(function (ms, i) {
      var leftPct = (ms.dollars / maxDollars) * 100;
      html += '<div class="bonus-bar__marker bonus-bar__marker--unreached" data-index="' + i + '" style="left:' + leftPct + '%">';
      html += '<span class="bonus-bar__marker-text">$' + ms.dollars + '</span>';
      html += '</div>';
    });

    html += '</div>'; // .bonus-bar__track
    html += '</div>'; // .bonus-bar__track-wrap
    return html;
  }

  /**
   * Ensure the content area has its inner markup.
   * After section refresh, the container is fresh and empty.
   */
  function ensureBuilt(bar) {
    var content = bar.querySelector('.bonus-bar__content');
    if (!content) return;
    if (!content.querySelector('.bonus-bar__track')) {
      content.innerHTML = buildContentHTML();
    }
  }

  /* ── Fetch cart total ──────────────────────────────────────────────── */
  function fetchCartTotal(callback) {
    fetch('/cart.js', {
      method: 'GET',
      headers: { 'Accept': 'application/json' }
    })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        var totalDollars = cart.total_price / 100;
        callback(totalDollars, cart.item_count);
      })
      .catch(function () {
        // Silently fail — bar keeps last known state
      });
  }

  /* ── Render / update the bar ───────────────────────────────────────── */
  function updateBar(totalDollars, itemCount) {
    var bar = document.getElementById('drawer-bonus-bar');
    if (!bar) return;

    // Store for morph recovery
    lastKnownTotal = totalDollars;
    lastKnownCount = itemCount;

    // Hide bar when cart is empty
    if (!itemCount || itemCount === 0 || totalDollars <= 0) {
      bar.style.display = 'none';
      return;
    }

    // Rebuild inner HTML if it was replaced by section refresh
    ensureBuilt(bar);
    // Re-attach observer in case morph replaced the content element
    watchForMorph(bar);

    bar.style.display = '';

    var maxDollars = getMaxDollars();
    var fillPct = Math.min((totalDollars / maxDollars) * 100, 100);

    // Update fill bar
    var fill = bar.querySelector('.bonus-bar__fill');
    if (fill) {
      fill.style.width = fillPct + '%';
    }

    // Update each milestone
    MILESTONES.forEach(function (ms, i) {
      var marker = bar.querySelector('.bonus-bar__marker[data-index="' + i + '"]');
      var label = bar.querySelector('.bonus-bar__label[data-index="' + i + '"]');
      if (!marker) return;

      var reached = totalDollars >= ms.dollars;
      marker.classList.toggle('bonus-bar__marker--reached', reached);
      marker.classList.toggle('bonus-bar__marker--unreached', !reached);

      if (label) {
        label.classList.toggle('bonus-bar__label--reached', reached);
        if (reached) {
          label.textContent = withCommas(ms.entries) + ' Free Entries';
        } else {
          label.textContent = withCommas(ms.entries) + ' Free Entries';
        }
      }
    });
  }

  /* ── Debounced refresh ─────────────────────────────────────────────── */
  function refresh() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function () {
      fetchCartTotal(updateBar);
    }, DEBOUNCE_MS);
  }

  /* ── Protect against morph clearing content ────────────────────────── */
  var contentObserver = null;
  var lastKnownTotal = 0;
  var lastKnownCount = 0;

  /**
   * Watch the .bonus-bar__content div for childList changes.
   * When morphSection() replaces the cart-items-component HTML,
   * it empties our dynamically-built content. This observer
   * fires synchronously and rebuilds the structure before the
   * browser paints, so the bar never visually disappears.
   */
  function watchForMorph(bar) {
    if (contentObserver) contentObserver.disconnect();

    var content = bar.querySelector('.bonus-bar__content');
    if (!content) return;

    contentObserver = new MutationObserver(function () {
      // If morph cleared our track, rebuild immediately
      if (!content.querySelector('.bonus-bar__track')) {
        content.innerHTML = buildContentHTML();
        // Re-apply last known values so bar doesn't reset to 0
        if (lastKnownCount > 0) {
          updateBar(lastKnownTotal, lastKnownCount);
        }
      }
    });

    contentObserver.observe(content, { childList: true });
  }

  /* ── Initialize ────────────────────────────────────────────────────── */
  function init() {
    var container = document.getElementById('drawer-bonus-bar');
    if (container) {
      ensureBuilt(container);
      watchForMorph(container);
      fetchCartTotal(updateBar);
    }

    // Only attach listeners once (script persists across section refreshes)
    if (listenersAttached) return;
    listenersAttached = true;

    // Theme dispatches 'cart:update' (no d), our code dispatches 'cart:updated'
    document.addEventListener('cart:update', refresh);
    document.addEventListener('cart:updated', refresh);
    document.addEventListener('cart:item-added', refresh);

    // Re-fetch when drawer dialog opens
    var drawer = document.querySelector('cart-drawer-component');
    if (drawer) {
      var dialog = drawer.querySelector('dialog');
      if (dialog) {
        var observer = new MutationObserver(function (mutations) {
          for (var i = 0; i < mutations.length; i++) {
            if (mutations[i].attributeName === 'open' && dialog.hasAttribute('open')) {
              refresh();
              break;
            }
          }
        });
        observer.observe(dialog, { attributes: true });
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
